import React, { ReactElement } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { GIButton } from 'app-components';
import { FormattedMessage } from 'react-intl';

import { ROOT_STATE } from '@store/store.types';
import { ctaHandlers } from '@utils/dashboard/stateSpecificUtils';
import {
    ALL_ELIGIBILITY_TYPES,
    DMI_DOCUMENT_UPLOAD_STATUS,
    VERIFICATION_STATUSES,
} from '@constants/individualPortal/dashboard/dashboardModelTypes';
import Tooltip from '@common-components/tooltip/Tooltip';
import states from '@constants/configs/states';
import { ConditionalRendering } from '@common-components/conditional-rendering';
import { IneligibilityReasons } from './IneligibilityReasons';
import {
    IProcessedApplicant,
    IuseEligibilityDetailsContainerProps,
} from '@constants/individualPortal/eligibility/eligibility.types';
import { IEX_STATE_CODES } from '@constants/configs/states';

const StyledTitle = styled.div`
    color: ${({ theme }) => theme.gray2};
    font-weight: 600;
    margin-bottom: 12px;
`;

export const useEligibilityDetailsContainer = (props: IuseEligibilityDetailsContainerProps) => {
    const { showActionNeeded, stateCode } = props;
    const eligibilityResults = useSelector((state: ROOT_STATE) => state.individualPortal.eligibilityResults);
    const displayEligibilityFactors = useSelector(
        (state: ROOT_STATE) => state.common.environment.flags.displayEligibilityFactors,
    );
    const environment = useSelector((state: ROOT_STATE) => state.common.environment);
    const primaryTaxFiler = React.useRef('');
    const {
        isMNFullStack,
    } = environment;
    const { remainingDaysForROP, caseNumber, isLatestApplication, showVoterRegistration, ssapApplicationId } = eligibilityResults;
    /**
     *  Returns type of action needed for Eligibility Details section (3d column).
     * @param exchangeEligible
     * @param assessedMedicaidMAGIEligible
     * @param assessedChipEligible
     * @param verificationStatus
     * @param caseNumber
     * @param isLatestApplication
     * @returns {*}
     */
    const getActionNeeded = (
        exchangeEligible: boolean,
        assessedMedicaidMAGIEligible: boolean,
        assessedChipEligible: boolean,
        verificationStatus: string,
        dmiDocumentUploadstatus: string,
        caseNumber: string,
        isLatestApplication: boolean,
        applicantGuid?: string,
    ) => {
        if (!isLatestApplication || !showActionNeeded) return false;

        if (
            verificationStatus === VERIFICATION_STATUSES.ACTION_NEEDED &&
            dmiDocumentUploadstatus === DMI_DOCUMENT_UPLOAD_STATUS.PENDING &&
            (exchangeEligible || assessedMedicaidMAGIEligible || assessedChipEligible)
        ) {
            return (
                <>
                    <StyledTitle>
                        <FormattedMessage id="eligibility_results.details.table_header3" />
                    </StyledTitle>
                    <GIButton
                        id="eligibilityDocsRequired"
                        onClickHandler={() =>
                            ctaHandlers.goToUploadDocs(caseNumber, stateCode, isMNFullStack, applicantGuid)
                        }
                        addlClassNames="usa-button usa-button--outline gi-button gi-button__outline eligibility-details__action--btn"
                        labelId="button.documents_required"
                        data-testid="eligibilityDocsRequired-button"
                    />
                    <p className="eligibility-details__action--note">
                        <FormattedMessage
                            id="eligibility_results.details.action_note1"
                            values={{ days: remainingDaysForROP }}
                        />
                    </p>
                </>
            );
        } else if (assessedMedicaidMAGIEligible || assessedChipEligible) {
            return (
                <p className="eligibility-details__action--note">
                    <FormattedMessage id="eligibility_results.details.action_note3" />
                </p>
            );
        } else if (
            verificationStatus === VERIFICATION_STATUSES.ACTION_NEEDED &&
            dmiDocumentUploadstatus === DMI_DOCUMENT_UPLOAD_STATUS.PROCESSING
        ) {
            return (
                <p className="eligibility-details__action--note">
                    <FormattedMessage id="eligibility_results.details.action_note2" />
                </p>
            );
        }
    };

    const renderEligibilityDetailsData = (
        type: React.Key | null | undefined,
        index: string | undefined,
        {
            assessedChipOrMedicaidEligible,
            aptcAndCsrNotEligible,
            chipOrMedicaidMAGIEligible,
        }: {
            assessedChipOrMedicaidEligible: boolean;
            chipOrMedicaidMAGIEligible: boolean;
            aptcAndCsrNotEligible: boolean;
        },
    ) => {
        let result: ReactElement = <></>;

        switch (type) {
            case ALL_ELIGIBILITY_TYPES.QHP:
                result = <FormattedMessage id="eligibility_results.details.market_health_dental" />;
                break;
            case ALL_ELIGIBILITY_TYPES.APTC:
                result = (
                    <Tooltip
                        id="indportal.dashboard.householdSection.eligibility.status.eligibleAptc"
                        content="household_section.tooltip_aptc"
                        index={index}
                    />
                );
                break;

            case ALL_ELIGIBILITY_TYPES.STATE_SUBSIDY:
                result = (
                    <Tooltip
                        id="household_section.eligibility.status.eligible_state_subsidy"
                        content="household_section.tooltip_state_subsidy"
                        index={index}
                        dataAutomationId={`state_subsidy_${index}`}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.STATE_SUBSIDY_2:
                result = (
                    <Tooltip
                        id="householdSection.eligibility.status.stateSubsidy2"
                        content="householdSection.tooltipStateSubsidy2"
                        index={index}
                        dataAutomationId={`state_subsidy_2_${index}`}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.STATE_SUBSIDY_3:
                result = (
                    <Tooltip
                        id="householdSection.eligibility.status.stateSubsidy3"
                        content="householdSection.tooltipStateSubsidy3"
                        index={index}
                        dataAutomationId={`state_subsidy_3_${index}`}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.CSR:
                result = (
                    <Tooltip
                        id="household_section.eligibility.status.csr"
                        content="household_section.tooltip_csr"
                        index={index}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.CSR_TURQUOISE:
                result = (
                    <span id={`eligibility_details_csr_with_turquoise_${index}`}>
                        <Tooltip
                            id="household_section.eligibility.status.csr"
                            content="household_section.tooltip_csr"
                            index={index}
                            dataAutomationId={`csr_turquoise_eligibility_details_${index}`}
                        />
                        <FormattedMessage id="householdSection.eligibility.status.withTurquoiseConnector" />
                        <Tooltip
                            id="householdSection.eligibility.status.turquoisePlans"
                            content="householdSection.tooltipTurquoisePlans"
                            index={index}
                            dataAutomationId={`turquoise_plans_eligibility_details_${index}`}
                        />
                    </span>
                );
                break;
            case ALL_ELIGIBILITY_TYPES.ASSESSED_MAGI_MEDICAID:
                result = <FormattedMessage id="household_section.eligibility.indicator.assessed_medicaid" />;
                break;
            case ALL_ELIGIBILITY_TYPES.MEDICAID_REFERRAL:
                result = (
                    <Tooltip
                        id="eligibility_results.details.medicaid_referral"
                        content="household_section.tooltip_medicaid_referral"
                        index={index}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.ASSESSED_CHIP:
                result = <FormattedMessage id="eligibility_results.details.assessed_chip" />;
                break;
            case ALL_ELIGIBILITY_TYPES.MEDICAID_OR_CHIP:
                result = <FormattedMessage id="eligibility_results.details.medicaid_or_chip" />;
                break;
            case ALL_ELIGIBILITY_TYPES.NOT_SEEKING:
                result = (
                    <Tooltip
                        id="household_section.eligibility.indicator.not_seeking_coverage"
                        content="household_section.tooltip_not_seeking"
                        index={index}
                    />
                );
                break;
            case ALL_ELIGIBILITY_TYPES.NOT_QHP: {
                if (stateCode === IEX_STATE_CODES.NV && assessedChipOrMedicaidEligible) {
                    result = (
                        <FormattedMessage id="eligibility_results.details.not_market_health_dental.assessed_chip_or_medicaid" />
                    );
                } else {
                    const messageId =
                        (stateCode === states.VIRGINIA || stateCode === states.GETINSURED) &&
                        (assessedChipOrMedicaidEligible || chipOrMedicaidMAGIEligible) &&
                        aptcAndCsrNotEligible
                            ? 'eligibility_results.details.not_market_health_dental.potential_medicaid'
                            : 'eligibility_results.details.not_market_health_dental';
                    result = (
                        <FormattedMessage
                            id={messageId}
                            values={{
                                notEligibleTooltip: (
                                    <Tooltip
                                        id="common.not_eligible"
                                        content="household_section.tooltip_not_exchange_eligible"
                                        index={index}
                                        replacement={{ exchangeName: environment.exchangeName }}
                                        contentReplacement={{ exchangeName: environment.exchangeName }}
                                    />
                                ),
                            }}
                        />
                    );
                }
                break;
            }
            case ALL_ELIGIBILITY_TYPES.POTENTIALLY_EMERGENCY_MEDICAID_ELIGIBLE:
                result = <FormattedMessage id="eligibility_results.details.potentially_emergency_medicaid_eligible" />;
                break;
            case ALL_ELIGIBILITY_TYPES.BHP:
                result = <FormattedMessage id="eligibility_results.details.bhp" />;
                break;
            case ALL_ELIGIBILITY_TYPES.ASSESSED_BHP:
                result = <FormattedMessage id="eligibility_results.details.assessedBhp" />;
                break;
            default:
                return result;
        }

        return result;
    };

    const getEligibilities = (
        member: IProcessedApplicant,
        assessedChipOrMedicaidEligible: boolean,
        chipOrMedicaidMAGIEligible: boolean,
        aptcAndCsrNotEligible: boolean,
        ineligibilityReasons: string[],
    ) => {
        const isIneligibilityFactorsVisible = displayEligibilityFactors && ineligibilityReasons?.length > 0;
        return (
            <>
                <StyledTitle>
                    <FormattedMessage id="eligibility_results.details.table_header2" />
                </StyledTitle>
                <ul className="eligibility-details__list">
                    {member.eligibilities.map((eligibility: React.Key | null | undefined, index) => {
                        const dataToRender = renderEligibilityDetailsData(eligibility, member.guid, {
                            assessedChipOrMedicaidEligible,
                            chipOrMedicaidMAGIEligible,
                            aptcAndCsrNotEligible,
                        });
                        const isEligibilityListItemVisible = dataToRender && dataToRender.props?.id;

                        return (
                            <ConditionalRendering isTrue={isEligibilityListItemVisible} key={`${eligibility}_${index}`}>
                                <li key={eligibility}>{dataToRender}</li>
                            </ConditionalRendering>
                        );
                    })}
                </ul>
                <ConditionalRendering isTrue={isIneligibilityFactorsVisible}>
                    <hr />
                    <IneligibilityReasons
                        ineligibilityReasons={ineligibilityReasons}
                        primaryTaxFiler={primaryTaxFiler.current}
                    />
                </ConditionalRendering>
            </>
        );
    };

    const getEligibilityDetails = (data: IProcessedApplicant[]) => {
        if (!data) return false;

        const ELIGIBILITY_DETAILS_HEADERS = [
            {
                id: 'name',
                text: (
                    <span className="usa-sr-only">
                        <FormattedMessage id="label.name" />
                    </span>
                ),
            },
            {
                id: 'eligibilities',
                text: (
                    <span className="usa-sr-only">
                        <FormattedMessage id="application.eligibility.Details.heading" />
                    </span>
                ),
            },
        ];

        showActionNeeded &&
            ELIGIBILITY_DETAILS_HEADERS.push({
                id: 'action',
                text: (
                    <span className="usa-sr-only">
                        <FormattedMessage id="screenpop.table.header.action" />
                    </span>
                ),
            });

        const eligibilityDetails = {
            columns: ELIGIBILITY_DETAILS_HEADERS,
            data: {},
        };

        eligibilityDetails.data = data.map((member: IProcessedApplicant) => {
            const {
                exchangeEligible,
                assessedMedicaidMAGIEligible,
                assessedChipEligible,
                verificationStatus,
                dmiDocumentUploadstatus,
                aptcEligible,
                csrEligible,
                chipEligible,
                medicaidMAGIEligible,
                ineligibilityReasons,
                taxHouseholdGroup,
                relation,
            } = member && member.rawData;

            if (relation === 'SELF') {
                primaryTaxFiler.current = member.name;
            }

            const assessedChipOrMedicaidEligible = assessedMedicaidMAGIEligible || assessedChipEligible;
            const chipOrMedicaidMAGIEligible = chipEligible || medicaidMAGIEligible;
            const aptcAndCsrNotEligible = !(aptcEligible || csrEligible);
            return {
                ...member,
                isCustomRender: taxHouseholdGroup !== 'NON_ITF' && member.rawData.seekingCoverage,
                eligibilities: getEligibilities(
                    member,
                    assessedChipOrMedicaidEligible,
                    chipOrMedicaidMAGIEligible,
                    aptcAndCsrNotEligible,
                    ineligibilityReasons,
                ),
                action: getActionNeeded(
                    exchangeEligible,
                    assessedMedicaidMAGIEligible,
                    assessedChipEligible,
                    verificationStatus,
                    dmiDocumentUploadstatus,
                    caseNumber,
                    isLatestApplication,
                    member.guid,
                ),
                customRenderMessage: (
                    <FormattedMessage
                        id="eligibility_results.details.itf_message"
                        values={{
                            member: member.name,
                            strong: function makeStrong(chunks) {
                                return <strong>{chunks}</strong>;
                            },
                        }}
                    />
                ),
            };
        });

        return eligibilityDetails;
    };

    return { getEligibilityDetails, showVoterRegistration, ssapApplicationId, caseNumber };
};
